import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CustomText } from '@4beefopag/4bee-fopag-core/dist/atoms';
import { Page } from '@4beefopag/4bee-fopag-core/dist/organisms';
import { imprimir } from '@4beefopag/4bee-fopag-core/dist/services';
import { AuthContext, useAuthHeader, useHttpRequest } from '@4beefopag/4bee-fopag-core/dist/services/hooks';
import { urlbaseBackend } from '@4beefopag/4bee-fopag-core/dist/util';
import { Col, Modal, Row } from 'antd';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import mostrarErro from '../../../services/util/error-handler';
import {
  APONTAMENTO,
  buscarParametrizacaoFolha,
  getRequisicoes,
  importarFile,
  PAYROLL_ACTOR_KIND,
} from '../../../services/apontamentoProducao';
import useExibicaoApontamento from '../../../services/apontamentoProducao/useExibicaoApontamento';
import { useApontamentoFilters } from '../../../services/apontamentoProducao/hooks/useApontamentoFilters';
import { useApontamentoRequisicao } from '../../../services/apontamentoProducao/hooks/useApontamentoRequisicao';
import { useApontamentoValidation } from '../../../services/apontamentoProducao/hooks/useApontamentoValidation';
import { useApontamentoProcessing } from '../../../services/apontamentoProducao/hooks/useApontamentoProcessing';
import { useApontamentoImport } from '../../../services/apontamentoProducao/hooks/useApontamentoImport';
import { applySelectionRulesToCollaborators } from '../../../services/apontamentoProducao/utils/apontamentoUtils';
import { updatePayrollItemValues } from '../../../services/apontamentoProducao/utils/mergeMonthlyItemValues';
import { sendContractToCooperator } from '../../../services/repository/cooperatorRepository';
import { cancel, createPayroll, removeById } from '../../../services/repository/payrollRepository';
import { useUserPolicy, Actions, Subjects } from '../../../user-policy';
import { checkCooperatorWithoutESocial } from './modals';
import { ContainerTela } from '../../atoms/Apontamento';
import ApiOriginBanner from '../../molecules/ApiOriginBanner';
import ApontamentoFilters from './ApontamentoFilters';
import ApontamentoTableContainer from './ApontamentoTableContainer';
import DrawerFinalizacao from '../../organisms/DrawerFinalizacao';
import ModalNovaRequisicao from '../../organisms/ModalNovaRequisicao';
import ModalReaberturaAdmin from '../../organisms/ModalReaberturaAdmin';
import ModalReaberturaGestor from '../../organisms/ModalReaberturaGestor';
import ModalRequestContractSigning from '../../organisms/ModalRequestContractSigning';
import ModalDivergenciaPlanilha from './ModaDivergenciaPlanilha';
import ModalCriarParametrizacao from './ModalCriarParametrizacao';
import { ModalDuplicateCooperators } from './ModalDuplicateCooperators';
import { ModalHiringModelMismatch } from './ModalHiringModelMismatch';
import { ModalCooperatorsQuantity } from './ModalCooperatorsQuantity';
import ModalLimiteReembolso from './ModalLimiteReembolso';
import { IMPORT_ERROR_TYPES } from '../../../services/apontamentoProducao/utils/importErrorConstants';

function ApontamentoProducao() {
  const { id: payrollIdParam } = useParams();
  const auth = useAuthHeader();
  const user = useContext(AuthContext);
  const { userAccount } = useUserPolicy();

  const canApproveReopening = Boolean(userAccount?.ability?.can(Actions.APPROVE, Subjects.PAYROLL_REOPENING));

  // Estados de modais
  const [isReaberturaModalOpen, setIsReaberturaModalOpen] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showExcessReimbursementModal, setShowExcessReimbursementModal] = useState(false);
  const [exceededReimbursementCollaborators, setExceededReimbursementCollaborators] = useState([]);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [showSendEmailWithContractModal, setShowSendEmailWithContractModal] = useState(false);
  const [duplicateErrorModal, setDuplicateErrorModal] = useState({ show: false, cooperatorsList: [] });
  const [hiringModelMismatchModal, setHiringModelMismatchModal] = useState({ visible: false, mismatches: [] });
  const [cooperatorsQuantityModal, setCooperatorsQuantityModal] = useState({ visible: false, cooperatorsList: [] });
  const [showCreateParameterizationModal, setShowCreateParameterizationModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [loading, setLoading] = useState({ confirm: false });
  const [divergenceModal, setDivergenceModal] = useState({
    visible: false,
    nonExistentFields: '',
    file: undefined,
    loading: false,
    sheetIndex: 0,
  });

  const [selectedCollaborators, setSelectedCollaborators] = useState([]);

  // Hook para gerenciar requisições (precisa vir primeiro para ter folha, competencia, tipo)
  const {
    payroll,
    setPayroll,
    loadingCollaborators,
    parameterization,
    setParameterization,
    company,
    setCompany,
    competence,
    setCompetence,
    type,
    setType,
    competences,
    fetchPayroll,
    selectParameterization,
    clearForm,
  } = useApontamentoRequisicao(payrollIdParam, setSelectedCollaborators);

  // Hook para buscar requisições (usa folha, competencia, tipo do hook acima)
  const {
    state: { data: payrolls, loading: loadingPayrolls },
    request: fetchPayrolls,
  } = useHttpRequest(
    async () =>
      getRequisicoes({
        folha: company?.id,
        competencia: competence,
        tipo: type,
      }),
    [],
    [company, competence, type]
  );

  // Estado de colaboradores selecionados
  const areAllSelectedCollaborators = !!(payroll?.items?.length === selectedCollaborators.length);

  // Hook para gerenciar filtros, paginação e totais
  const {
    filterName,
    setFilterName,
    pagination,
    goToFirstPage,
    changePagination,
    changePaginationSize,
    filteredCollaboratorsPagination,
    orderBy,
    setOrderBy,
    totals,
  } = useApontamentoFilters(payroll, selectedCollaborators);

  // Hook para validações
  const validations = useApontamentoValidation();

  // Função para aplicar regras de seleção
  const applySelectionRules = useCallback(
    () => applySelectionRulesToCollaborators(payroll, selectedCollaborators),
    [payroll, selectedCollaborators]
  );

  // Hook para processamento
  const {
    finalizarProcessamento: finalizeProcessing,
    onSalvar: onSave,
    onAtualizar: onUpdate,
  } = useApontamentoProcessing(
    payroll,
    type,
    parameterization,
    user,
    applySelectionRules,
    fetchPayroll,
    setLoadingModal,
    setShowProcessModal,
    setSelectedCollaborators
  );

  // Hook para importação
  const { exibirAlertaSucesso: showSuccessAlert } = useApontamentoImport();

  // Hook para exibição
  const [exibicao] = useExibicaoApontamento(payroll || {}, user, payrollIdParam);
  const display = exibicao;

  const handleCloseReabertura = useCallback(() => {
    setIsReaberturaModalOpen(false);
  }, []);

  const handleSuccessReabertura = useCallback(async () => {
    setIsReaberturaModalOpen(false);
    await fetchPayroll(payroll.id, setSelectedCollaborators);
  }, [fetchPayroll, payroll?.id]);

  // Efeito para verificar colaboradores sem eSocial (payroll?.id evita reabrir ao paginar:
  // novas linhas montam ColaboradorMensal que recalcula totais e atualiza o objeto payroll)
  useEffect(() => {
    if (!payroll?.id) return;
    checkCooperatorWithoutESocial({ requisicao: payroll, selectedColaboradores: selectedCollaborators });
  }, [selectedCollaborators, payroll?.id]);

  // Handler para mudança de folha
  const handlePayrollSheetChange = useCallback(
    (value) => {
      if (payrollIdParam) return;
      if (!value) {
        clearForm();
        return;
      }
      const currentMonth = moment();
      setCompany(value);
      setPayroll(undefined);
      const formattedCompetence = currentMonth.format('MM/YYYY');
      setCompetence(formattedCompetence);
      selectParameterization(value.idFolha, currentMonth);
    },
    [selectParameterization]
  );

  // Handler para mudança de competência
  const handleCompetenceChange = useCallback(
    async (value) => {
      setCompetence(value);
      setPayroll(undefined);
      const param = await buscarParametrizacaoFolha(company?.id, moment(value, 'MM/YYYY'));
      setParameterization(param);
    },
    [setCompetence, setPayroll, company, setParameterization]
  );

  // Handler para mudança de tipo
  const handleTypeChange = useCallback(
    (newType) => {
      setPayroll(undefined);
      setType(newType);
    },
    [setPayroll, setType]
  );

  // Handler para mudança de requisição
  const handlePayrollChange = useCallback(
    async (payrollId) => {
      const payrollFound = await fetchPayroll(payrollId, setSelectedCollaborators);
      validations.contemApontamentoComColaboradorSemContratoAssinado(payrollFound, parameterization);

      let errorMessage;
      if (payrollFound.requisicao && user.isGestor()) {
        if (payrollFound.requisicao.cancelada) {
          errorMessage = {
            titulo: 'Requisição cancelada',
            mensagem: 'Não será possível fazer alterações nessa requisição, pois a requisição foi cancelada.',
          };
        } else if (
          payrollFound.requisicao.status === APONTAMENTO.status.rascunho &&
          payrollFound.requisicao.dataPrevistaFatura !== null
        ) {
          errorMessage = {
            titulo: 'Pagamento realizado',
            mensagem: 'Não será possível fazer alterações nessa requisição, pois o processamento já foi efetuado',
          };
        }
      }

      if (errorMessage) {
        Modal.warning({
          title: (
            <CustomText
              color="var(--text-grey-8)"
              fontSize={16}
            />
          ),
          content: <p>{errorMessage.mensagem}</p>,
          centered: true,
          width: 560,
        });
      }
    },
    [fetchPayroll, validations, parameterization, user]
  );

  // Handler para criar nova requisição
  const handleNewPayroll = useCallback(
    async (description) => {
      setLoading({ ...loading, confirm: true });
      if (company) {
        try {
          const payrollCreated = await createPayroll({
            folha: company?.id,
            competencia: competence,
            tipo: type,
            descricao: description,
          });
          setPayroll(payrollCreated);
          setSelectedCollaborators(payrollCreated.items.map(({ cooperator }) => cooperator.id));
          await fetchPayrolls();
          const param = await buscarParametrizacaoFolha(
            payrollCreated.folha.id,
            moment(payrollCreated.competencia, 'YYYY-MM-DD')
          );
          setParameterization(param);
        } catch (err) {
          mostrarErro(err);
        } finally {
          setShowNewRequestModal(false);
          setLoading({ ...loading, confirm: false });
        }
      }
    },
    [company, competence, type, setPayroll, setSelectedCollaborators, fetchPayrolls, setParameterization, loading]
  );

  // Handler atômico: usa estado confirmado e consolida campo + totais na mesma atualização
  const handleChangeItemValues = useCallback(
    (itemId, newValue) => {
      setPayroll((currentPayroll) => updatePayrollItemValues(currentPayroll, itemId, newValue));
    },
    [setPayroll]
  );

  // Handler para selecionar todos os colaboradores
  const handleOnChangeAllSelected = useCallback(
    (selectAll) => {
      const selected = selectAll ? payroll.items.map(({ cooperator }) => cooperator.id) : [];
      setSelectedCollaborators(selected);
      goToFirstPage();
    },
    [payroll, setSelectedCollaborators, goToFirstPage]
  );

  // Handler para mudança de colaboradores
  const handleCollaboratorsChange = useCallback(
    (values) => {
      setSelectedCollaborators(values);
      goToFirstPage();
    },
    [setSelectedCollaborators, goToFirstPage]
  );

  // Função para validar se pode processar
  const canProcess = useCallback(async () => {
    const getFilteredItems = () =>
      (payroll?.items?.filter(({ cooperator }) => selectedCollaborators.includes(cooperator.id)) || []).map((item) => ({
        ...item,
        requisicao: payroll.id,
      }));

    const collaboratorsToProcess = getFilteredItems();

    if (type === APONTAMENTO.tiposRequisicoes.mensal) {
      if (!validations.canProccessMensal(collaboratorsToProcess, setOrderBy)) {
        return false;
      }
    }

    if (type === APONTAMENTO.tiposRequisicoes.adiantamento) {
      if (!validations.canProccessAdiantamento(collaboratorsToProcess)) {
        return false;
      }
    }

    if (!parameterization) {
      return false;
    }

    const emailValidation = await validations.validaEmailParametrizado(parameterization);
    if (!emailValidation.ok) {
      return false;
    }

    await validations.validarContratos(payroll, selectedCollaborators, parameterization);

    const exceededReimbursement = validations.getColaboradoresExcedeLimiteReembolso(collaboratorsToProcess);
    setExceededReimbursementCollaborators(exceededReimbursement);
    if (exceededReimbursement.length) {
      setShowExcessReimbursementModal(true);
      return false;
    }

    return true;
  }, [type, payroll, selectedCollaborators, parameterization, validations, setOrderBy]);

  // Handler para processar
  const handleProcess = useCallback(async () => {
    const canContinue = await canProcess();
    if (canContinue) {
      setShowProcessModal(true);
    }
  }, [canProcess]);

  // Handler para exportar Excel
  const handleExportExcel = useCallback(async () => {
    if (payroll.status === APONTAMENTO.status.rascunho) {
      await onSave(false);
    }

    let fileName = 'relApontamento';
    if (company && competence !== undefined && competence !== null) {
      const payrollSheetDescription = company.descricao.replace(/[^\w\s]/g, '_');
      fileName = `${moment(competence, 'MM/YYYY').format('MM_YYYY')}_${type}_${payrollSheetDescription}`;
    }

    return new Promise((resolve, reject) => {
      imprimir(payroll, '/apontamento/exportar', `${fileName}.xls`)
        .then(() => {
          Modal.success({
            title: 'Sucesso',
            content: <p>Apontamento exportado com sucesso!</p>,
          });
          resolve();
        })
        .catch((e) => {
          mostrarErro(e);
          reject();
        });
    });
  }, [payroll, company, competence, type, onSave]);

  // Handler para importar Excel
  // Recebe resultado do tratamento de erros ou resposta de sucesso
  const handleImportExcel = useCallback(
    async (response, file, sheetIndex) => {
      // Se response.data existe e tem type, é um erro tratado que requer ação especial
      const errorData = response?.data;

      // Erros que exigem modal declarativo
      if (errorData?.type === IMPORT_ERROR_TYPES.DUPLICATE_COOPERATORS) {
        setDuplicateErrorModal({
          show: true,
          file: errorData.file || file,
          cooperatorsList: errorData.cooperatorsList || [],
        });
        return;
      }
      if (errorData?.type === IMPORT_ERROR_TYPES.MISSING_COLUMNS) {
        setDivergenceModal({
          visible: true,
          nonExistentFields: errorData.missingFields || '',
          file: errorData.file || file,
          sheetIndex: errorData.sheetIndex ?? sheetIndex,
        });
        return;
      }
      if (errorData?.type === IMPORT_ERROR_TYPES.HIRING_MODEL_MISMATCH) {
        setHiringModelMismatchModal({
          visible: true,
          file: errorData.file || file,
          mismatches: errorData.mismatches || [],
        });
        return;
      }
      if (errorData?.type === IMPORT_ERROR_TYPES.SHEET_SCREEN_MISMATCH) {
        setCooperatorsQuantityModal({
          visible: true,
          file: errorData.file || file,
          cooperatorsList: errorData.cooperatorsList || [],
        });
        return;
      }

      // Se chegou aqui, importação foi bem-sucedida
      await fetchPayroll(payroll.id);
      showSuccessAlert();
    },
    [fetchPayroll, payroll?.id, showSuccessAlert]
  );

  // Handler para exibir modal de cooperados sem contrato
  const handleBeforeUploadModal = useCallback(async () => {
    try {
      const validation = await validations.validarContratos(payroll, selectedCollaborators, parameterization);
      if (!validation.ok) {
        setLoadingModal(false);
        setShowProcessModal(false);
        return true;
      }
      return false;
    } catch (err) {
      return true;
    }
  }, [validations, payroll, selectedCollaborators, parameterization]);

  // Handler para cancelar requisição
  const handleCancelRequest = useCallback(async () => {
    try {
      await cancel(payroll.id);
      Modal.success({
        title: 'Sucesso',
        content: <p>Requisição cancelada com sucesso!</p>,
      });
      await fetchPayrolls();
      await fetchPayroll(payroll.id, setSelectedCollaborators);
    } catch (err) {
      mostrarErro(err);
    }
  }, [payroll, fetchPayrolls, fetchPayroll, setSelectedCollaborators]);

  // Handler para deletar requisição
  const handleDeleteRequest = useCallback(async () => {
    try {
      await removeById(payroll.id);
      Modal.success({
        title: 'Sucesso',
        content: <p>Requisição removida com sucesso!</p>,
      });
      await fetchPayrolls();
      setPayroll(undefined);
      setFilterName(undefined);
      setSelectedCollaborators([]);
    } catch (err) {
      mostrarErro(err);
    }
  }, [payroll, fetchPayrolls, setPayroll, setFilterName, setSelectedCollaborators]);

  // Handler para confirmar envio de email de contrato
  const handleConfirmSendEmailToSigningContract = useCallback(
    async (cooperatorIds) => {
      try {
        await sendContractToCooperator(company?.id, cooperatorIds);
        setShowSendEmailWithContractModal(false);
        Modal.success({
          title: 'Sucesso',
          content: <p>E-mail(s) enviado(s) para o(s) cooperado(s) selecionado(s)</p>,
        });
      } catch (err) {
        mostrarErro(err);
      }
    },
    [company]
  );

  // Handler para finalizar processamento
  const handleFinalizeProcessing = useCallback(
    (expectedDate, expectedInvoiceDate, observation) => {
      setLoadingModal(true);
      const request = {
        ...payroll,
        expectedDate,
        expectedInvoiceDate,
        observacao: observation,
      };
      finalizeProcessing(request, false);
    },
    [payroll, finalizeProcessing]
  );

  const handleModalImportIgnoreError = useCallback(
    async (cb, file) => {
      try {
        await importarFile({
          arquivo: file,
          idRequisicao: payroll.id,
          ignoreValidation: true,
          sheetIndex: divergenceModal.sheetIndex,
        });
        if (cb) {
          cb();
        }
        showSuccessAlert();
        await fetchPayroll(payroll.id, setSelectedCollaborators);
      } catch (err) {
        mostrarErro(err);
      }
    },
    [divergenceModal, payroll, type, company, fetchPayroll, setSelectedCollaborators, showSuccessAlert]
  );

  // Handler para modal de divergências
  const handleDivergenceModalOk = useCallback(
    async (fields) => {
      setDivergenceModal({ ...divergenceModal, loading: true });
      try {
        await importarFile({
          arquivo: divergenceModal.file,
          idRequisicao: payroll.id,
          campos: fields,
          ignoreValidation: false,
          sheetIndex: divergenceModal.sheetIndex,
        });

        setDivergenceModal({ ...divergenceModal, visible: false });
        showSuccessAlert();
        await fetchPayroll(payroll.id, setSelectedCollaborators);
      } catch (err) {
        mostrarErro(err);
      } finally {
        setDivergenceModal({
          visible: false,
          nonExistentFields: '',
          file: undefined,
          loading: false,
          sheetIndex: 0,
        });
      }
    },
    [divergenceModal, payroll, type, company, fetchPayroll, setSelectedCollaborators, showSuccessAlert]
  );

  return (
    <Page
      projeto="Cooperativa"
      titulo="Apontamento de Produção"
      linkManual={process.env.REACT_APP_LINK_MANUAL}
    >
      {/* Modal de Criar Parametrização */}
      {showCreateParameterizationModal && (
        <ModalCriarParametrizacao
          folha={company}
          competencia={moment(competence, 'MM/YYYY')}
          onCloseModal={() => setShowCreateParameterizationModal(false)}
          onCreatedParam={(createdParam) => selectParameterization(createdParam.id_folha, createdParam.vigencia)}
          onEditParam={() => {
            window.open(`/apontamento/parametrizar/${parameterization ? parameterization.id : ''}`, '_blank').focus();
          }}
        />
      )}
      {/* Filtros do Apontamento */}
      <ApontamentoFilters
        payrollSheet={company}
        competence={competence}
        competences={competences}
        type={type}
        payroll={payroll}
        payrolls={payrolls}
        loadingPayrolls={loadingPayrolls}
        parameterization={parameterization}
        filterName={filterName}
        selectedCollaborators={selectedCollaborators}
        areAllSelectedCollaborators={areAllSelectedCollaborators}
        display={display}
        onPayrollSheetChange={handlePayrollSheetChange}
        onCompetenceChange={handleCompetenceChange}
        onTypeChange={handleTypeChange}
        onPayrollChange={handlePayrollChange}
        onNewPayrollClick={() => setShowNewRequestModal(true)}
        onFilterNameChange={setFilterName}
        onCollaboratorsChange={handleCollaboratorsChange}
        onCollaboratorsSelectAll={handleOnChangeAllSelected}
        onGoToFirstPage={goToFirstPage}
      />

      {/* Container Principal */}
      <ContainerTela>
        {parameterization?.nome && (
          <Row>
            <Col
              xs={24}
              sm={24}
              md={24}
              lg={24}
            >
              <span
                style={{
                  display: 'inline-flex',
                  flexWrap: 'wrap',
                  alignItems: 'baseline',
                  columnGap: '0.25rem',
                }}
              >
                <CustomText
                  fontSize="14px"
                  color="var(--text-grey-8)"
                  ellipsis
                >
                  Parametrização vigente:{' '}
                </CustomText>
                <a
                  href={`/apontamento/parametrizar/${parameterization.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {parameterization.nome}
                </a>
              </span>
            </Col>
          </Row>
        )}

        {payroll?.createdByActorKind === PAYROLL_ACTOR_KIND.M2M && <ApiOriginBanner criadoEm={payroll.criadoEm} />}

        {/* Container da Tabela */}
        <ApontamentoTableContainer
          loadingCollaborators={loadingCollaborators}
          parameterization={parameterization}
          payrollSheet={company}
          competence={competence}
          payroll={payroll}
          type={type}
          filteredCollaboratorsPagination={filteredCollaboratorsPagination}
          pagination={pagination}
          totals={totals}
          orderBy={orderBy}
          onChangeItemValues={handleChangeItemValues}
          onChangeOrder={setOrderBy}
          onChangePagination={changePagination}
          onChangePaginationSize={changePaginationSize}
          onSave={onSave}
          onProcess={handleProcess}
          onExportExcel={handleExportExcel}
          onImportExcel={handleImportExcel}
          onRequestContractSigning={() => setShowSendEmailWithContractModal(true)}
          onCancel={handleCancelRequest}
          onDelete={handleDeleteRequest}
          onUpdate={onUpdate}
          onReopen={() => setIsReaberturaModalOpen(true)}
          onBeforeUploadModal={handleBeforeUploadModal}
          display={display}
          authHeaders={{
            ...auth,
            idRequisicao: payroll?.id,
            tipo: type,
            idFolha: company?.id,
            campos: [],
            validarCampos: true,
            urlAction: urlbaseBackend('apontamento/importar'),
          }}
          onShowCreateParameterizationModal={setShowCreateParameterizationModal}
        />
      </ContainerTela>

      {/* Modais */}
      <DrawerFinalizacao
        visible={showProcessModal}
        onCancel={() => {
          setShowProcessModal(false);
          setLoadingModal(false);
        }}
        onOk={handleFinalizeProcessing}
        loading={loadingModal}
        requisicao={payroll}
      />

      <ModalDivergenciaPlanilha
        visible={divergenceModal.visible}
        onCancel={() => {
          setDivergenceModal({
            ...divergenceModal,
            visible: false,
            nonExistentFields: '',
            file: undefined,
            loading: false,
          });
        }}
        onOk={handleDivergenceModalOk}
        loading={divergenceModal.loading}
        camposNaoExistentes={divergenceModal.nonExistentFields}
      />

      <ModalDuplicateCooperators
        visible={duplicateErrorModal.show}
        listaCooperados={duplicateErrorModal.cooperatorsList}
        onOk={() => setDuplicateErrorModal({ show: false, cooperatorsList: [] })}
      />

      <ModalHiringModelMismatch
        visible={hiringModelMismatchModal.visible}
        mismatches={hiringModelMismatchModal.mismatches}
        onOk={() =>
          handleModalImportIgnoreError(
            () => setHiringModelMismatchModal({ visible: false, mismatches: [] }),
            hiringModelMismatchModal.file
          )
        }
        onCancel={() => setHiringModelMismatchModal({ visible: false, mismatches: [] })}
      />

      <ModalCooperatorsQuantity
        visible={cooperatorsQuantityModal.visible}
        cooperators={cooperatorsQuantityModal.cooperatorsList}
        onOk={() =>
          handleModalImportIgnoreError(
            () => setCooperatorsQuantityModal({ visible: false, cooperatorsList: [] }),
            cooperatorsQuantityModal.file
          )
        }
        onCancel={() => setCooperatorsQuantityModal({ visible: false, cooperatorsList: [] })}
      />

      <ModalRequestContractSigning
        visible={showSendEmailWithContractModal}
        onCancel={() => setShowSendEmailWithContractModal(false)}
        handleConfirmClick={handleConfirmSendEmailToSigningContract}
        colaboradores={payroll?.items.filter(({ cooperator }) => !cooperator.hasSignedContract)}
      />

      {canApproveReopening ? (
        <ModalReaberturaAdmin
          visible={isReaberturaModalOpen}
          onCancel={handleCloseReabertura}
          onSuccessful={handleSuccessReabertura}
          onError={handleCloseReabertura}
          payroll={payroll}
        />
      ) : (
        <ModalReaberturaGestor
          visible={isReaberturaModalOpen}
          onCancel={handleCloseReabertura}
          onSuccessful={handleSuccessReabertura}
          onError={handleCloseReabertura}
          payroll={payroll}
        />
      )}

      <ModalNovaRequisicao
        visible={showNewRequestModal}
        onOk={handleNewPayroll}
        onCancel={() => setShowNewRequestModal(false)}
        loading={loading.confirm}
      />

      <ModalLimiteReembolso
        visible={showExcessReimbursementModal}
        colaboradores={exceededReimbursementCollaborators}
        requisicao={payroll}
        onOk={({ anexos }) => {
          setPayroll({ ...payroll, anexos });
          setShowExcessReimbursementModal(false);
          setShowProcessModal(true);
        }}
        onCancel={() => setShowExcessReimbursementModal(false)}
      />
    </Page>
  );
}

export default ApontamentoProducao;
